import { LitElement, html, unsafeCSS } from "lit-element"
import { classMap } from "lit-html/directives/class-map"
import commonStyle from "../common.css"
import style from "./menu.css"

/**
 * メニュー部品
 */
export default class extends LitElement {
	static styles = [unsafeCSS(style), unsafeCSS(commonStyle)]
	static get properties () {
		return {
			position: { type: String },
			secondary: { type: Boolean }
		}
	}

	/**
	 * メニューに表示するアイテムのグループ
	 * @typedef {Object} ItemGroup
	 * @property {Item[]} items
	 */
	/**
	 * メニューに表示するアイテム
	 * @typedef {Object} Item
	 * @property {string|null} icon
	 * @property {string} text
	 * @property {string} value
	 * @property {boolean} disabled
	 * @property {Function} onClick
	 * @property {Item[]} subItems
	 */

	/**
	 * @type {ItemGroup[]}
	 */
	#itemGroups = []

	/**
	 * @type {number} サブメニューに割り当てるidの元
	 */
	#id = 0

	/**
	 * @type {HTMLElement} このメニューが利用しているポップアップコンポーネント
	 */
	#popup = null

	/**
	 * @type {boolean} サブメニューの場合false
	 */
	isRoot = true

	/**
	 * @type {Function}
	 */
	#eventRemover = null

	/**
	 * @type {number} サブメニューに割り当てるid
	 */
	get #newId () {
		return `menu_${++this.#id}`
	}

	constructor () {
		super()
		this.secondary = false
	}

	render () {
		return html`
			<bui-popup ?static=${this.isRoot}>
				<div class=${this.#menuClass} @hide-request="${this.hideIfRoot}">
					${this.#itemGroups.map((itemGroup, idx, groupList) => this.#renderItemGroups(itemGroup, idx, groupList))}
				</div>
			</bui-popup>
			<style>
				${commonStyle}
				${style}
			</style>
		`
	}

	disconnectedCallback () {
		super.disconnectedCallback()
		// コンテキストメニューイベントを解除
		this.unbind()
	}

	get #menuClass () {
		return classMap({
			"bui-list": true,
			menu: true,
			dense: true,
			surface: true,
			shadow: true,
			"is-secondary": this.secondary
		})
	}

	/**
	 * アイテムグループ描画
	 * @param {ItemGroup} itemGroup
	 * @param {number} idx
	 * @param {ItemGroup[]} groupList
	 */
	#renderItemGroups (itemGroup, idx, groupList) {
		const groupHasIcon = itemGroup.items.some(item => !!item.icon)
		const isLastItem = groupList.length === idx + 1
		return html`
			${itemGroup.items.map(item => this.#renderItem(item, groupHasIcon))}
			${isLastItem ? "" : html`<div class="bui-list-divider">`}
		`
	}

	/**
	 * アイテム、サブアイテム描画
	 * @param {Item} item
	 * @param {boolean} groupHasIcon
	 */
	#renderItem (item, groupHasIcon) {
		let itemHtml
		if (item.subItems) {
			itemHtml = html`
			<div class="bui-list-item-wrapper"
				@mouseover="${(e) => { this.#openSubMenu(item.id, item.subItems, e) }}"
				@mouseleave="${() => { this.#hideSubMenu(item.id) }}"
			>
				<button 
					is="bui-button"
					class="bui-list-item has-submenu"
					?disabled=${item.disabled}
				>
					${item.icon ? html`<div class="bui-list-item-icon"><bui-icon class="icon" icon=${item.icon}></bui-icon></div>` : ""}
					${!item.icon && groupHasIcon ? html`<div class="bui-list-item-icon is-empty"></div>` : ""}
					<div class="bui-list-item-content">${item.text}</div>
				</button>
				<bui-menu id=${item.id} ?secondary=${this.secondary}></bui-menu>
			</div>
			`
		} else {
			itemHtml = html`
			<button
				is="bui-button"
				class="bui-list-item hl"
				@click="${(e) => { this.#itemClick(item.onClick, item.value) }}"
				?disabled=${item.disabled}
			>
				${item.icon ? html`<div class="bui-list-item-icon"><bui-icon class="icon" icon=${item.icon}></bui-icon></div>` : ""}
				${!item.icon && groupHasIcon ? html`<div class="bui-list-item-icon is-empty"></div>` : ""}
				<div class="bui-list-item-content">${item.text}</div>
			</button>
			`
		}

		return itemHtml
	}

	firstUpdated () {
		this.#popup = this.shadowRoot.querySelector("bui-popup")
	}

	#openSubMenu (id, items, event) {
		const menu = this.shadowRoot.getElementById(id)
		menu.isRoot = false
		menu.init([{ items }]).then(() => {
			menu.showAtTarget({
				target: event.currentTarget,
				position: "bottom-right",
				offsetX: true,
				additionalOffsetY: -4 // メニューのpadding-top分、上にずらす
			})
		})
	}

	#hideSubMenu (id) {
		const menu = this.shadowRoot.getElementById(id)
		menu.hide()
	}

	#itemClick (func, event) {
		if (func instanceof Function) func(event)
		if (this.isRoot) {
			this.hide()
		} else {
			this.dispatchEvent(new CustomEvent("hide-request", {
				// shadow DOMの境界を通してバブリングするようにする
				bubbles: true,
				composed: true
			}))
		}
	}

	/**
	 * メニュー初期化処理
	 * @param {ItemGroup[]} itemGroups
	 * @return {Promise}
	 */
	init (itemGroups = []) {
		this.#itemGroups = itemGroups
		// itemにIDを払い出す
		this.#itemGroups.forEach(itemGroup => {
			itemGroup.items.forEach(item => {
				item.id = this.#newId
			})
		})
		return this.requestUpdate()
	}

	/**
	 *
	 * @param {Object} args
	 */
	showAtTarget (args) {
		if (!args.position) args.position = "bottom-left"
		this.#popup.showAtTarget(args)
	}

	/**
	 * 指定された要素にコンテキストメニューイベントを登録する
	 * @param {HTMLElement} el
	 */
	bind (el) {
		const showContextmenu = (e) => {
			e.preventDefault()
			this.#popup.showAtCursor(e)
		}
		el.addEventListener("contextmenu", showContextmenu)

		this.#eventRemover = () => {
			el.removeEventListener("contextmenu", showContextmenu)
		}
	}

	/**
	 * コンテキストメニューイベントを解除する
	 */
	unbind () {
		if (this.#eventRemover) this.#eventRemover()
		this.#eventRemover = null
	}

	/**
	 * 自身とサブメニューすべてを閉じる
	 */
	hide () {
		// サブメニューのhideを再帰的に呼び出す
		this.shadowRoot.querySelectorAll("bui-menu").forEach(el => {
			el.hide()
		})
		// サブメニューを閉じたら自分も閉じる
		this.#popup.hide()
	}

	/**
	 * 自身がサブメニューでなければ閉じる
	 */
	hideIfRoot () {
		if (this.isRoot) this.hide()
	}
}
